import {
  TREATMENT_RUNTIME_STATE,
} from "./treatmentApiConstants";
import { isCanonicalConsentQuestion } from "./consentQuestionClassification";

// Common Section fields are published with a stable section_id. Older
// questionnaire projections used element_config.sourceSectionId, so keep
// that alias supported while preserving Program-scoped questions derived
// from a Section as Program questions.
export const isCommonSectionQuestion = (question) => {
  if (!question || question.scope === "program") return false;
  const sectionId = (
    question.section_id
    || question.source_section_id
    || question.sourceSectionId
    || question.element_config?.sourceSectionId
    || question.element_config?.source_section_id
  );
  return Boolean(String(sectionId || "").trim());
};

export const authorityStateForQuestion = (question) => {
  if (!question) return null;
  if (question.question_type === "matched_program_selection") {
    return question.matching_config?.stage === "recommendations"
      ? TREATMENT_RUNTIME_STATE.recommendations
      : TREATMENT_RUNTIME_STATE.potentialMatches;
  }
  if (
    isCanonicalConsentQuestion(question)
    && !isCommonSectionQuestion(question)
    // Inline consent authored inside a Program is part of that Program's
    // screening state. Assigned/global consents may carry a program_id for
    // visibility, but they are still the standalone consent boundary.
  ) {
    return TREATMENT_RUNTIME_STATE.consents;
  }
  if (
    question.question_type === "program_lab_checkout"
  ) {
    return TREATMENT_RUNTIME_STATE.labSelection;
  }
  if (
    question.question_type === "shipping_address"
    && question.location_role === "shipping"
    && Number(question.location_contract_version || 0) >= 4
  ) {
    return TREATMENT_RUNTIME_STATE.shipping;
  }
  if (
    question.question_type === "product_selection"
    || question.question_type === "checkout"
    || (
      question.question_type === "shipping_address"
      && question.location_role !== "patient_service"
    )
  ) {
    return TREATMENT_RUNTIME_STATE.productSelection;
  }
  // An authored Service Area Check can carry a Program ID for availability
  // context, but its answer is still owned by the routing boundary.  Keep it
  // beside the patient-service address so both are submitted atomically in a
  // single answer_routing transition.
  if (question.question_type === "state_routing") {
    return TREATMENT_RUNTIME_STATE.routing;
  }
  if (
    question.program_id
    || question.question_type === "treatment_screening_intro"
  ) {
    return TREATMENT_RUNTIME_STATE.treatmentScreening;
  }
  // `routing_profile` means the answer may also populate matching/profile
  // context; it does not move an explicitly authored Common Section field
  // into the routing boundary. Matching occurs after Common screening.
  if (question.scope === "common") {
    return TREATMENT_RUNTIME_STATE.commonScreening;
  }
  return TREATMENT_RUNTIME_STATE.routing;
};
